import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface CreateFeedbackIssueDto {
  summary: string;
  description: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  severity?: 'Low' | 'Medium' | 'High' | 'Critical';
  entityType?: string;
  entityId?: string | number;
  environment?: string;
  pageUrl?: string;
  source?: string;
  issueType?: 'Bug' | 'Task' | 'Story' | 'Epic';
}

export interface JiraIssueResponse {
  key: string;
  id: string;
  url: string;
}

export interface ContentIssue {
  key: string;
  summary: string;
  priority: string | null;
  reporter: string | null;
  assignee: string | null;
  status: string | null;
  sprint: string | null;
  url: string;
  source: string;
  ticketCreatedAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  constructor(private readonly http: HttpClient) {}

  async createIssue(dto: CreateFeedbackIssueDto): Promise<JiraIssueResponse> {
    return firstValueFrom(
      this.http.post<JiraIssueResponse>('/api/feedback/issue', dto),
    );
  }

  async addAttachment(issueKey: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    return firstValueFrom(
      this.http.post<any>(`/api/feedback/issue/${issueKey}/attachment`, form),
    );
  }

  async getIssueTransitions(issueKey: string): Promise<string[]> {
    return firstValueFrom(this.http.get<string[]>(`/api/feedback/issue/${issueKey}/transitions`));
  }

  async updateIssueStatus(issueKey: string, status: string): Promise<void> {
    await firstValueFrom(
      this.http.patch(`/api/feedback/issue/${issueKey}/status`, { status }),
    );
  }

  async getIssuesByEntity(entityType: string, entityId: string | number): Promise<any[]> {
    return firstValueFrom(
      this.http.get<any[]>(`/api/feedback/issues/by-entity?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`),
    );
  }

  async getIssuesByWeek(source: string, weekStart?: Date): Promise<ContentIssue[]> {
    let params = `?source=${encodeURIComponent(source)}`;
    if (weekStart) {
      params += `&weekStart=${weekStart.toISOString().split('T')[0]}`;
    }
    return firstValueFrom(
      this.http.get<ContentIssue[]>(`/api/feedback/issues${params}`),
    );
  }
}
